import { Reducer } from 'redux';
import deepmerge from 'deepmerge';
import { DocStatus } from 'api-paths-cas';
import { IDocumentState, DocActionTypes } from './types';
import { StudentActionTypes } from '../types';
import { DataActionTypes } from '../../data/types';
import { UIActionTypes } from '../../ui/types';
import { IAnyObj } from '../../../types';
import { transformObject } from './helpers';
import { getDocDetails } from '../../../utilities/documents';

const initialState: IDocumentState = {
  cas: {},
  passport: {},
  academicTranscript: {},
  academicCertificate: {},
  studentVisas: {},
  visaRefusals: {},
  nonUkVisaRefusals: {},
  otherVisas: {},
  parentalConsent: {},
  birthCertificate: {},
  tbTest: {},
  atas: {},
  englishTest: {},
  englishQualification: {},
  customRequest: {},
  postStudyLetter: {},
  uploadedSuccessfully: false,
  fileUploadError: null,
  deletedSuccessfully: false,
  internalUploadedSuccessfully: false,
  internalFileUploadError: null,
};

const overwriteMerge = (_: [], sourceArray: []) => sourceArray;

const mainReducer: Reducer<IDocumentState> = (state = initialState, action) => {
  switch (action.type) {
    case StudentActionTypes.GET_STUDENT_DOCS_SUCCESS: {
      const documents = transformObject(action.payload.documents);
      return {
        ...initialState,
        ...documents,
      };
    }
    case StudentActionTypes.SAVE_PERSONAL_INFO_SUCCESS:
    case StudentActionTypes.SAVE_HISTORY_SUCCESS:
    case StudentActionTypes.SAVE_CONSENTS_SUCCESS:
    case StudentActionTypes.SAVE_PRE_CAS_INTERVIEW_SUCCESS:
    case StudentActionTypes.SAVE_VISA_PREP_FORM_SUCCESS:
    case DocActionTypes.UPDATE_VISA_REFUSAL_META_DATA_SUCCESS:
    case StudentActionTypes.UPDATE_CONFLICT_SUCCESS:
    case StudentActionTypes.UPDATE_OFFER_EVIDENCE_SUCCESS:
    case DocActionTypes.UPDATE_TB_SUCCESS: {
      if (action.payload.documents) {
        return {
          ...initialState,
          ...action.payload.documents,
        };
      }
      return state;
    }
    case StudentActionTypes.DELETE_DOCUMENT_FILE_SUCCESS: {
      if (action.payload?.documents) {
        const documents = transformObject(action.payload.documents);
        const newState = deepmerge({ ...state }, documents, {
          arrayMerge: overwriteMerge,
        });
        return {
          ...newState,
        };
      }
      return state;
    }

    case StudentActionTypes.UPDATE_DOCUMENTS: {
      if (action.payload?.docs) {
        const newState = deepmerge({ ...state }, action.payload.docs, {
          arrayMerge: overwriteMerge,
        });
        return {
          ...newState,
        };
      }
      return state;
    }

    case UIActionTypes.OPEN_UPLOAD_MODAL: {
      const { sponsorType, sponsorKey, docType, docKey, fileNames, metadata } = action.payload;

      if (!metadata) {
        return state;
      }
      const newState = { ...state };
      const newRecord = getDocDetails({ docs: newState, sponsorType, sponsorKey, docType, docKey }) as IAnyObj;

      // extend the object with new metadata, no need to create new object ref
      Object.entries(metadata).forEach(([key, value]) => {
        if (key === 'isDocumentFixed') {
          // don't update state as fixed until file(s) have been uploaded
          newRecord.isDocumentFixedPending = value;
          newRecord.docCount = fileNames.length;
          if (newRecord.isDocumentFixed) {
            delete newRecord.isDocumentFixed;
          }
        } else if (value !== undefined) {
          newRecord[key] = value;
        }
      });

      return {
        ...newState,
      };
    }

    case StudentActionTypes.DELETE_FILE: {
      const { sponsorType, sponsorKey, docType, docKey, fileNames, isGeneralDocumentModal } = action.payload;

      if (!isGeneralDocumentModal) {
        return state;
      }
      const newState = { ...state };
      const newRecord = getDocDetails({ docs: newState, sponsorType, sponsorKey, docType, docKey }) as IAnyObj;
      newRecord.fileNames = fileNames;

      return {
        ...newState,
      };
    }

    case DataActionTypes.UPLOAD_FILE_SUCCESS: {
      const {
        sponsorType,
        sponsorKey,
        docType,
        docKey,
        fileNames,
        isFinanceDocumentModal,
        isFollowUpDocumentModal,
        isInterviewDocumentModal,
        isRecordedInterviewDocumentModal,
      } = action.payload;

      if (
        isFinanceDocumentModal ||
        isFollowUpDocumentModal ||
        isInterviewDocumentModal ||
        isRecordedInterviewDocumentModal
      ) {
        return state;
      }
      // TODO -for student if hasErrors
      // has errors ?
      const status = DocStatus.UPLOADED_OK;
      const newState = { ...state };

      console.log('newRecord get');
      const newRecord = getDocDetails({ docs: newState, sponsorType, sponsorKey, docType, docKey });
      console.log('newRecord', newRecord);

      // strip out file upload failures
      let parsedFileNames: string[] = fileNames;
      parsedFileNames = fileNames?.filter((fName: string) => !fName.includes('upload--error--')) ?? [];
      newRecord.fileNames = parsedFileNames;

      newRecord.status = status;

      return {
        ...newState,
      };
    }

    case StudentActionTypes.SAVE_DOCUMENTS_SUCCESS:
    case DocActionTypes.TOGGLE_DOC_SUCCESS: {
      const docs = transformObject(action.payload.docs || action.payload.documents);
      // merge the delta
      const newState = deepmerge(state, docs, {
        arrayMerge: overwriteMerge,
      });
      return {
        ...newState,
      };
    }

    case DocActionTypes.SAVE_DOCUMENT: {
      return {
        ...state,
        uploadedSuccessfully: false,
        fileUploadError: null,
      };
    }

    case DocActionTypes.SAVE_DOCUMENT_SUCCESS: {
      return {
        ...state,
        uploadedSuccessfully: true,
        fileUploadError: null,
      };
    }

    case DocActionTypes.SAVE_DOCUMENT_FAILURE: {
      return {
        ...state,
        uploadedSuccessfully: false,
        fileUploadError: action.payload.error,
      };
    }

    case DocActionTypes.SAVE_INTERNAL_DOCUMENT: {
      return {
        ...state,
        internalUploadedSuccessfully: false,
        internalFileUploadError: null,
      };
    }

    case DocActionTypes.SAVE_INTERNAL_DOCUMENT_SUCCESS: {
      return {
        ...state,
        internalUploadedSuccessfully: true,
        internalFileUploadError: null,
      };
    }

    case DocActionTypes.SAVE_INTERNAL_DOCUMENT_FAILURE: {
      return {
        ...state,
        internalUploadedSuccessfully: false,
        internalFileUploadError: action.payload.error,
      };
    }

    case DocActionTypes.DELETE_DOCUMENT: {
      return {
        ...state,
        deletedSuccessfully: false,
        fileUploadError: null,
      };
    }

    case DocActionTypes.DELETE_DOCUMENT_SUCCESS: {
      return {
        ...state,
        deletedSuccessfully: true,
        fileUploadError: null,
      };
    }

    case DocActionTypes.DELETE_DOCUMENT_FAILURE: {
      return {
        ...state,
        deletedSuccessfully: false,
        fileUploadError: action.payload.error,
      };
    }

    default:
      return state;
  }
};

export { mainReducer as documentsReducer };
